/**
 * Auth DTOs (login / refresh / register) and the Principal shape.
 *
 * These are provider-agnostic: the `JwtAuthProvider` uses login/register/refresh
 * directly; OAuth/Supabase providers verify an external token and produce the
 * same `Principal`. Regardless of provider, the dashboard sends the resulting
 * access token as `Authorization: Bearer` to the backend.
 */
import type { z } from 'zod';
import { email, literal, number, object, string } from 'zod';

import { RoleSchema } from './enums.js';
import { UserSchema } from './organizations.js';

/**
 * The authenticated principal carried in the JWT and resolved by
 * `AuthProvider.authenticate()`. `orgId` is null for meta-admin.
 */
export const PrincipalSchema = object({
  userId: string().min(1),
  role: RoleSchema,
  orgId: string().nullable(),
  /**
   * The project (widget instance) a PROJECT_ADMIN is attached to; null for every
   * other role. Optional on input with a null default ON PURPOSE: access tokens
   * issued before this field existed are still in browsers, and a required claim
   * would fail `PrincipalSchema.safeParse` and sign every live session out.
   */
  projectId: string().nullable().optional().default(null),
});
export type Principal = z.infer<typeof PrincipalSchema>;

/**
 * `GET /auth/me` — who am I, and what may I do, answered from the DATABASE.
 *
 * Both halves matter and neither can come from the access token alone:
 *  - `principal` must be re-derived from the stored user, because a token
 *    carries the role its holder had when it was MINTED. Demote someone, or
 *    delete them, and a claims-only answer keeps honouring the old power until
 *    the token expires.
 *  - `user` carries the display identity (name, email). Without it a client
 *    that restores a session has nothing to show but the raw user id.
 */
export const MeResponseSchema = object({
  principal: PrincipalSchema,
  user: UserSchema,
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Login with email + password (local JWT provider). */
export const LoginRequestSchema = object({
  email: email(),
  password: string().min(1).max(1024),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** Self-registration / first-run admin bootstrap (local JWT provider). */
export const RegisterRequestSchema = object({
  email: email(),
  password: string().min(8).max(1024),
  name: string().max(256).optional(),
  /** Optional org name to provision alongside the first org-admin. */
  organizationName: string().min(1).max(256).optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/** Exchange a refresh token for a fresh access (+ rotated refresh) token. */
export const RefreshRequestSchema = object({
  refreshToken: string().min(1),
});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

/**
 * Sign in with Google. `credential` is the Google Identity Services ID token
 * (a JWT) returned to the browser by the "Sign in with Google" button; the
 * backend verifies it (JWKS / aud / iss / email_verified) and mints Hotline
 * tokens. These are additive login methods — the issued tokens are the same
 * Hotline JWTs as email+password, so the guard chain is unchanged.
 */
export const GoogleLoginRequestSchema = object({
  credential: string().min(1).max(8192),
});
export type GoogleLoginRequest = z.infer<typeof GoogleLoginRequestSchema>;

/**
 * Request a passwordless magic-link email. `redirectPath` is an optional in-app
 * path to land on after sign-in (validated to a relative path on the client).
 * The endpoint always responds 202 regardless of whether the email exists
 * (anti-enumeration) and never returns the link.
 */
export const MagicLinkRequestSchema = object({
  email: email(),
  redirectPath: string().max(512).optional(),
});
export type MagicLinkRequest = z.infer<typeof MagicLinkRequestSchema>;

/** The opaque single-use token carried in the magic-link URL (`?token=…`). */
export const MagicLinkVerifyRequestSchema = object({
  token: string().min(1).max(512),
});
export type MagicLinkVerifyRequest = z.infer<typeof MagicLinkVerifyRequestSchema>;

/** Acknowledgement for a magic-link request (never reveals account existence). */
export const MagicLinkRequestResultSchema = object({
  ok: literal(true),
});
export type MagicLinkRequestResult = z.infer<typeof MagicLinkRequestResultSchema>;

/** Token pair issued on login / register / refresh. */
export const TokenPairSchema = object({
  accessToken: string().min(1),
  refreshToken: string().min(1),
  /** Access-token lifetime in seconds. */
  expiresIn: number().int().positive(),
  tokenType: literal('Bearer').default('Bearer'),
});
export type TokenPair = z.infer<typeof TokenPairSchema>;

/** Full auth response: tokens + the safe user projection. */
export const AuthResponseSchema = object({
  tokens: TokenPairSchema,
  user: UserSchema,
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;
